import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';

import { CloudSun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import { getOperationalWeatherLocationLabel, HANOI_WEATHER_REFERENCE } from '@/config/weather';
import { useGetOpenMeteoPointCurrentQuery } from '@/features/dashboard/api/openMeteoApi';
import { WeatherForecastModal } from '@/features/dashboard/components/WeatherForecastModal';
import { useAppSelector } from '@/hooks/useAppSelector';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

const ACCENT = '#38bdf8';

const SHELL: CSSProperties = {
  background: 'var(--map-chrome-panel)',
  backdropFilter: 'blur(16px)',
  border: '1px solid var(--glass-border)',
  borderRadius: 12,
  padding: '10px 12px',
  flex: '1 1 140px',
  minWidth: 120,
  maxWidth: 220,
  boxSizing: 'border-box',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  pointerEvents: 'auto',
};

/**
 * Thời tiết theo tâm bản đồ — BFF Open-Meteo `kind=current`, hiển thị dạng thẻ KPI trên bản đồ.
 */
export function WeatherKpiCard() {
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.auth.user);
  const scenarioMode = useAppSelector((s) => s.scenario.mode);
  const weatherPoint = useAppSelector((s) => s.map.weatherPoint);
  const isLive = scenarioMode === 'live';
  const fetchOps = shouldFetchOperationalApi();
  const [forecastOpen, setForecastOpen] = useState(false);

  const envFixedWeatherCoords = env.WEATHER_DEFAULT_LAT != null && env.WEATHER_DEFAULT_LON != null;

  const { latStr, lonStr } = useMemo(
    () => ({
      latStr: (weatherPoint?.lat ?? HANOI_WEATHER_REFERENCE.lat).toFixed(4),
      lonStr: (weatherPoint?.lng ?? HANOI_WEATHER_REFERENCE.lng).toFixed(4),
    }),
    [weatherPoint],
  );

  const skipMeteo = !user || !fetchOps || !isLive;
  const { data: om, isFetching } = useGetOpenMeteoPointCurrentQuery(
    { lat: latStr, lon: lonStr },
    { skip: skipMeteo },
  );

  if (!user || !fetchOps || !isLive) {
    return null;
  }

  const locationLabel = envFixedWeatherCoords ? getOperationalWeatherLocationLabel() : null;
  const useDefaultHanoi = !weatherPoint && !envFixedWeatherCoords;

  const tempShort = (() => {
    const v = om?.current?.temperature_2m;
    if (v != null) return `${Math.round(v * 10) / 10}°`;
    if (isFetching) return '…';
    return '';
  })();

  const footerPlace =
    locationLabel ??
    (useDefaultHanoi
      ? t('kpi.weatherDefaultPlace')
      : om?.place_name ??
        (om ? `${om.latitude.toFixed(2)}°, ${om.longitude.toFixed(2)}°` : undefined));

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={forecastOpen}
        aria-label={t('weather.headerButtonAria')}
        onClick={() => setForecastOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setForecastOpen(true);
          }
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.borderColor = `${ACCENT}66`;
          e.currentTarget.style.boxShadow = `0 12px 40px ${ACCENT}1a`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.borderColor = forecastOpen ? `${ACCENT}66` : 'var(--glass-border)';
          e.currentTarget.style.boxShadow = forecastOpen
            ? `0 12px 40px ${ACCENT}1a`
            : 'var(--shadow-lg)';
        }}
        style={{
          ...SHELL,
          borderColor: forecastOpen ? `${ACCENT}66` : 'var(--glass-border)',
          boxShadow: forecastOpen ? `0 12px 40px ${ACCENT}1a` : 'var(--shadow-lg)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              paddingRight: 2,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: 1.35,
            }}
          >
            {t('kpi.weatherAtPoint')}
          </span>
          <div
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: 6,
              background: `${ACCENT}15`,
              border: `1px solid ${ACCENT}33`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloudSun size={12} strokeWidth={2.2} color={forecastOpen ? ACCENT : ACCENT} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.3px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {tempShort ? tempShort : t('weather.headerShortLabel')}
          </span>
        </div>

        {footerPlace ? (
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: 'var(--text-muted)',
              lineHeight: 1.35,
              marginTop: -2,
            }}
          >
            {footerPlace}
          </div>
        ) : null}
      </div>

      <WeatherForecastModal
        open={forecastOpen}
        onClose={() => setForecastOpen(false)}
        latStr={latStr}
        lonStr={lonStr}
        locationLabelOverride={locationLabel}
      />
    </>
  );
}
